import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { FileText, Scale, Banknote, Clock, XCircle, CheckCircle, Mail, Phone } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="section-container">
          {/* Header */}
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">
                  Terms of Business
                </h1>
                <p className="text-muted-foreground">Last updated: January 2025</p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                These Terms of Business set out the basis on which Blue Lion Law Limited will provide legal services 
                to you in connection with your car finance mis-selling claim.
              </p>

              {/* Section 1 */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <Scale className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    1. About Us
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <p>
                    Blue Lion Law Limited is a limited company registered in England and Wales (Company Number 12276560), 
                    authorised and regulated by the Solicitors Regulation Authority (SRA) under SRA number 815166.
                  </p>
                  <p>
                    Our registered office is: The Centro Buildings, 20-23 Mandela Street, London, NW1 0DU.
                  </p>
                </div>
              </section>

              {/* Section 2 */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    2. Our Services
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <p>We will:</p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Assess your eligibility for a car finance mis-selling claim</li>
                    <li>Investigate your finance agreements and gather evidence</li>
                    <li>Submit complaints to lenders on your behalf</li>
                    <li>Negotiate with lenders to recover compensation</li>
                    <li>If necessary, escalate your complaint to the Financial Ombudsman Service</li>
                    <li>Keep you informed of progress throughout</li>
                  </ul>
                </div>
              </section>

              {/* Section 3 - Fees */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <Banknote className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    3. Our Fees - No Win, No Fee
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <p>
                    We operate on a <strong>"No Win, No Fee"</strong> basis. This means:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>You will not pay anything upfront</li>
                    <li>If your claim is unsuccessful, you will not owe us any fees</li>
                    <li>If your claim is successful, our fee will be deducted from your compensation</li>
                  </ul>
                  
                  <div className="mt-6 p-4 rounded-lg bg-gold/10 border border-gold/30">
                    <h3 className="font-semibold text-foreground mb-2">Success Fee</h3>
                    <p>
                      If we recover compensation for you, our success fee is <strong>a maximum of 36% (inclusive of VAT)</strong> of 
                      the total compensation recovered. This is the maximum fee permitted by the FCA for claims management services.
                    </p>
                  </div>

                  <p className="mt-4">
                    <strong>Example:</strong> If we recover £1,000 compensation, our maximum fee would be £360 (inclusive of VAT), 
                    and you would receive £640.
                  </p>
                </div>
              </section>

              {/* Section 4 - Cooling Off */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    4. Your Right to Cancel (Cooling-Off Period)
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <p>
                    You have a <strong>14-day cooling-off period</strong> from the date you sign our engagement letter. 
                    During this period, you can cancel our services without charge and without any obligation.
                  </p>
                  <p>
                    To cancel, please contact us in writing by email or post.
                  </p>
                </div>
              </section>

              {/* Section 5 - Cancellation */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <XCircle className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    5. Cancellation After Cooling-Off Period
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <p>
                    You may cancel our services at any time after the cooling-off period. However, if you cancel after 
                    we have commenced work on your claim, you may be liable for our reasonable costs incurred up to 
                    the date of cancellation.
                  </p>
                  <p>
                    Any cancellation fee will be reasonable and reflect the work we have already undertaken on your behalf.
                  </p>
                </div>
              </section>

              {/* Section 6 */}
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl font-bold text-foreground m-0">
                    6. Your Responsibilities
                  </h2>
                </div>
                <div className="pl-8 space-y-4 text-foreground/80">
                  <p>You agree to:</p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Provide accurate and complete information</li>
                    <li>Respond promptly to our requests for information</li>
                    <li>Notify us of any changes to your contact details</li>
                    <li>Not engage another firm to pursue the same claim</li>
                    <li>Cooperate fully with us throughout the claims process</li>
                  </ul>
                </div>
              </section>

              {/* Alternative Options Notice */}
              <section className="mt-8 p-6 rounded-xl bg-accent/10 border border-accent/30">
                <h2 className="font-heading text-xl font-bold text-foreground mb-4">
                  Important: You Can Claim Directly
                </h2>
                <p className="text-foreground/80 mb-4">
                  As stated by the FCA, you do not need to use a law firm or claims management company to make a 
                  car finance mis-selling claim. You are entitled to:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-foreground/80">
                  <li>Complain directly to your lender at no cost</li>
                  <li>Escalate to the Financial Ombudsman Service at no cost if you are unhappy with the response</li>
                  <li>Take part in any FCA compensation scheme when it begins (expected early 2026)</li>
                </ul>
                <p className="mt-4 text-foreground/80">
                  By choosing to use our services, you may end up paying fees out of any compensation received.
                </p>
              </section>

              {/* Contact */}
              <section className="mt-12 p-6 rounded-xl bg-muted/50 border border-border">
                <h2 className="font-heading text-xl font-bold text-foreground mb-4">
                  Contact Us
                </h2>
                <p className="text-foreground/80 mb-4">
                  If you have any questions about these Terms of Business, please contact us:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-accent" />
                    <a href="mailto:contactus@bluelionclaims.co.uk" className="text-accent hover:underline">
                      contactus@bluelionclaims.co.uk
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-accent" />
                    <a href="tel:02081530033" className="text-accent hover:underline">
                      0208 153 0033
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsPage;
